import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../../firebase/AuthProvider';
import { Message } from '../../../services/chatService/ChatService';
import { useImagePreview } from '../../../contexts/ImagePreviewContext';
import { downloadAttachment } from '../../../utilities/fileUtils';
import pdficon from '../../../assets/images/pdficon.svg';
import excelicon from '../../../assets/images/excelicon.svg';
import wordfileicon from '../../../assets/images/wordfileicon.svg';
import imgfileicon from '../../../assets/images/imgfileicon.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/free-solid-svg-icons';

const SpinnerIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={`animate-spin ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);

interface AttachmentDisplayProps {
    message: Message;
    attachmentUrl: string;
    attachmentType: string;
}

const extensionToIcon: { [key: string]: string } = {
    'pdf': pdficon,
    'xls': excelicon,
    'xlsx': excelicon,
    'excel': excelicon,
    'doc': wordfileicon,
    'docx': wordfileicon,
};

const AttachmentDisplay: React.FC<AttachmentDisplayProps> = ({ message }) => {
    const { currentUser } = useAuth();
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { open: openImagePreview } = useImagePreview();

    const isImage = message.attachmentType?.startsWith('image');

    const fetchAttachmentBlob = useCallback(async (): Promise<Blob> => {
        if (!currentUser || !message.id) throw new Error("Authentication or message ID is missing.");

        const token = await currentUser.getIdToken();
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/chat/message/${message.id}/attachment`;

        const response = await fetch(apiUrl, {
            headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);
        return response.blob();
    }, [currentUser, message.id]);

    useEffect(() => {
        if (!isImage) return;

        let objectUrl: string | undefined;

        const fetchAndDisplayImage = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const blob = await fetchAttachmentBlob();
                objectUrl = URL.createObjectURL(blob);
                setImageSrc(objectUrl);
            } catch (err) {
                console.error("Failed to load attachment image:", err);
                setError("Failed to load image.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchAndDisplayImage();

        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [isImage, fetchAttachmentBlob]);

    const handleDownload = () => {
        try {
            downloadAttachment(message);
        } catch (err) {
            setError("Download failed.");
        }
    };

    const getFileIcon = (fileType: string) => {
        if (!fileType) return <FontAwesomeIcon icon={faFile} className="text-gray-500 text-2xl" />;
        const iconSrc = extensionToIcon[fileType.toLowerCase()];
        if (iconSrc) {
            return <img src={iconSrc} alt="file icon" className="w-full h-full object-contain" />;
        }
        return <FontAwesomeIcon icon={faFile} className="text-gray-500 text-2xl" />;
    };

    if (isImage) {
        if (isLoading) {
            return (
                <div className="mt-2 flex items-center justify-center h-40 w-60 bg-gray-100 dark:bg-gray-700 rounded-md">
                    <SpinnerIcon className="h-8 w-8 text-gray-500" />
                </div>
            );
        }

        if (error || !imageSrc) {
            return (
                <div className="mt-2">
                    <button onClick={handleDownload} className="text-blue-500 hover:underline inline-flex items-center gap-2 p-2 bg-gray-100 dark:bg-gray-700 rounded-md">
                        <img src={imgfileicon} alt="file icon" className="w-5 h-5" />
                        <span>Download Image</span>
                    </button>
                </div>
            );
        }

        return (
            <button
                onClick={() => openImagePreview(imageSrc, message.attachmentType, message)}
                className="mt-2 block cursor-pointer appearance-none bg-transparent border-none p-0"
                aria-label="Enlarge image"
            >
                <img
                    src={imageSrc}
                    alt={message.attachmentType || 'attachment'}
                    className="max-h-60 rounded-md object-cover hover:opacity-80 transition-opacity"
                />
            </button>
        );
    }

    // Fallback for other file types
    const fileType = message.attachmentType || "file";
    const fileIcon = getFileIcon(fileType);
    const displayName = message.attachmentType ? `${message.attachmentType.toUpperCase()} File` : "File";

    return (
        <div className="mt-1 mr-2 mb-1 inline-block">
            <button
                onClick={handleDownload}
                className="p-2 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 flex items-center justify-start align-middle gap-2"
                style={{width: '180px'}}
                title={`Download ${displayName}`}
            >
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-white rounded-lg p-1">
                    {fileIcon}
                </div>
                <p className="truncate text-sm font-medium text-gray-800 overflow-hidden my-auto">{displayName}</p>
            </button>
        </div>
    );
};

export default AttachmentDisplay;